import React, { useState, useRef, useEffect, useContext, useCallback } from "react"
import { Box, IconButton, TextField } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SendIcon from "@mui/icons-material/Send"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import APIS from "../utils/APIS"
import LanguageContext from "../contexts/LanguageContext"

/* Chat de support maison — remplace Crisp.
 *
 * Volontairement autonome : aucun script tiers, aucune dependance ajoutee, et il
 * fonctionne sans etre connecte (le cas d'usage numero un est justement
 * quelqu'un bloque au login). Le back applique un plafond horaire par IP.
 *
 * Interface facon outils de support (Aircall/Intercom) :
 *  - chaque message porte son AUTEUR et son HEURE, avec un separateur de date
 *    quand la conversation s'etale sur plusieurs jours ;
 *  - avatar et statut « en ligne » dans l'en-tete, indicateur de frappe anime ;
 *  - une escalade est affichee explicitement, pour que la personne sache qu'un
 *    humain prend le relais ;
 *  - tant que le chat est ouvert, on sonde le serveur toutes les 8 s pour
 *    afficher les reponses humaines (avec leur vrai horodatage serveur).
 */

const INTERVALLE_SONDAGE = 8000

const TEXTES = {
  fr: {
    titre: "Support Rising Up",
    statut: "En ligne · réponse en quelques minutes",
    accueil: "Bonjour, comment puis-je vous aider\u00a0?",
    saisie: "Écrivez votre question…",
    envoyer: "Envoyer",
    ouvrir: "Ouvrir le support",
    fermer: "Fermer",
    vous: "Vous",
    assistant: "Assistant",
    equipe: "Équipe Rising Up",
    aujourdhui: "Aujourd'hui",
    escalade: "Un membre de l'équipe a été prévenu et reviendra vers vous.",
    erreur: "Le support est momentanément indisponible. Réessayez dans quelques minutes.",
    trop: "Vous avez envoyé beaucoup de messages. Merci de patienter un moment.",
    locale: "fr-FR",
  },
  en: {
    titre: "Rising Up support",
    statut: "Online · replies within minutes",
    accueil: "Hello, how can I help you?",
    saisie: "Type your question…",
    envoyer: "Send",
    ouvrir: "Open support",
    fermer: "Close",
    vous: "You",
    assistant: "Assistant",
    equipe: "Rising Up team",
    aujourdhui: "Today",
    escalade: "A team member has been notified and will get back to you.",
    erreur: "Support is temporarily unavailable. Please try again in a few minutes.",
    trop: "You have sent a lot of messages. Please wait a moment.",
    locale: "en-GB",
  },
}

/* Animations injectees une fois : les styles inline ne savent pas declarer de
 * @keyframes. Prefixe supportchat- pour ne rien percuter. */
const STYLES_ANIMES = `
@keyframes supportchat-dot {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30% { transform: translateY(-4px); opacity: 1; }
}
@keyframes supportchat-pop {
  from { transform: translateY(6px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .supportchat-anime { animation: none !important; }
}`

const Avatar = ({ taille = 26 }) => (
  <Box
    style={{
      width: taille, height: taille, minWidth: taille, borderRadius: "50%",
      backgroundColor: "#fff", color: "#000", border: "1px solid #ECE7E3",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: taille * 0.38, fontWeight: 700, letterSpacing: 0.5,
    }}
  >
    RU
  </Box>
)

const SupportChat = ({ plateforme = "lab", ouvertParDefaut = false, onClose }) => {
  const langue = useContext(LanguageContext)
  const t = TEXTES[langue?.actLanguage === "en" ? "en" : "fr"] || TEXTES.fr

  const [ouvert, setOuvert] = useState(ouvertParDefaut)
  const [messages, setMessages] = useState([
    { id: 0, role: "assistant", contenu: t.accueil, quand: new Date() },
  ])
  const [saisie, setSaisie] = useState("")
  const [enCours, setEnCours] = useState(false)
  const finDuFil = useRef(null)

  // Refs plutot que state : le sondage lit ces valeurs sans se re-declencher a
  // chaque render, et dernierID evite d'afficher deux fois le meme message.
  const conversationRef = useRef(null)
  const jetonRef = useRef(null)
  const dernierIDRef = useRef(0)

  useEffect(() => {
    if (ouvert && finDuFil.current) {
      finDuFil.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages, ouvert, enCours])

  useEffect(() => { setOuvert(ouvertParDefaut) }, [ouvertParDefaut])

  // Sondage des reponses humaines. Seuls les messages de role 'humain' sont
  // ajoutes : les messages user/assistant sont deja affiches localement.
  const sonder = useCallback(async () => {
    if (!conversationRef.current) return
    const r = await APIS.supportFil({
      conversationID: conversationRef.current,
      jeton: jetonRef.current,
      depuisID: dernierIDRef.current,
    })
    if (!r || !r.isOk || !Array.isArray(r.messages) || !r.messages.length) return
    const nouveaux = r.messages.filter((m) => m.id > dernierIDRef.current)
    if (!nouveaux.length) return
    dernierIDRef.current = nouveaux[nouveaux.length - 1].id
    const humains = nouveaux
      .filter((m) => m.role === "humain")
      .map((m) => ({
        id: `h-${m.id}`, role: "assistant", contenu: m.contenu, humain: true,
        quand: m.createdAt ? new Date(m.createdAt) : new Date(),
      }))
    if (humains.length) setMessages((prev) => [...prev, ...humains])
  }, [])

  useEffect(() => {
    if (!ouvert) return undefined
    const timer = setInterval(sonder, INTERVALLE_SONDAGE)
    return () => clearInterval(timer)
  }, [ouvert, sonder])

  const envoyer = async () => {
    const texte = saisie.trim()
    if (!texte || enCours) return

    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", contenu: texte, quand: new Date() }])
    setSaisie("")
    setEnCours(true)

    const r = await APIS.supportMessage({
      message: texte,
      conversationID: conversationRef.current,
      jeton: jetonRef.current,
      plateforme,
    })

    if (!r || r.error) {
      const message = r && r.data === "tropDeMessages" ? t.trop : t.erreur
      setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", contenu: message, systeme: true, quand: new Date() }])
    } else {
      if (r.conversationID) conversationRef.current = r.conversationID
      if (r.jeton) jetonRef.current = r.jeton
      setMessages((m) => [
        ...m,
        { id: `r-${Date.now()}`, role: "assistant", contenu: r.reponse, quand: new Date() },
        ...(r.escalade ? [{ id: `s-${Date.now()}`, role: "assistant", contenu: t.escalade, systeme: true, quand: new Date() }] : []),
      ])
    }
    setEnCours(false)
  }

  const fermer = () => {
    setOuvert(false)
    if (onClose) onClose()
  }

  const heureDe = (d) => {
    if (!d) return ""
    try { return new Date(d).toLocaleTimeString(t.locale, { hour: "2-digit", minute: "2-digit" }) } catch (e) { return "" }
  }
  const jourDe = (d) => {
    const date = new Date(d)
    if (date.toDateString() === new Date().toDateString()) return t.aujourdhui
    try { return date.toLocaleDateString(t.locale, { weekday: "long", day: "numeric", month: "long" }) } catch (e) { return "" }
  }
  const auteurDe = (m) => (m.role === "user" ? t.vous : m.humain ? t.equipe : t.assistant)

  if (!ouvert) {
    return (
      <IconButton
        aria-label={t.ouvrir}
        onClick={() => setOuvert(true)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1300,
          backgroundColor: "#000", color: "#fff", width: 56, height: 56,
          boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
        }}
      >
        <ChatBubbleOutlineIcon />
      </IconButton>
    )
  }

  return (
    <Box
      role="dialog"
      aria-label={t.titre}
      style={{
        position: "fixed", bottom: 24, right: 24, zIndex: 1300,
        width: 372, maxWidth: "calc(100vw - 32px)", height: 520, maxHeight: "calc(100vh - 48px)",
        display: "flex", flexDirection: "column",
        backgroundColor: "#fff", borderRadius: 14,
        boxShadow: "0 12px 40px rgba(0,0,0,0.22)", overflow: "hidden",
      }}
    >
      <style>{STYLES_ANIMES}</style>

      <Box style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "14px 10px 14px 16px", backgroundColor: "#000", color: "#fff",
      }}>
        <Avatar taille={34} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>{t.titre}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "#B9B9B9", marginTop: 2 }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%", backgroundColor: "#3DBE7B", display: "inline-block",
            }} />
            {t.statut}
          </div>
        </Box>
        <IconButton aria-label={t.fermer} onClick={fermer} size="small" style={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box style={{ flex: 1, overflowY: "auto", padding: "14px 12px", backgroundColor: "#FAF8F6" }}>
        {messages.map((m, i) => {
          const precedent = messages[i - 1]
          const nouveauJour = !precedent || new Date(precedent.quand).toDateString() !== new Date(m.quand).toDateString()
          const memeAuteurQuAvant = precedent && !nouveauJour && auteurDe(precedent) === auteurDe(m) && !m.systeme && !precedent.systeme

          if (m.systeme) {
            return (
              <Box key={m.id} className="supportchat-anime" style={{ animation: "supportchat-pop 0.25s ease-out", textAlign: "center", margin: "10px 0" }}>
                <span style={{
                  display: "inline-block", fontSize: 12, color: "#7A6A4F", backgroundColor: "#FFF4E5",
                  border: "1px solid #F2E3C8", borderRadius: 20, padding: "5px 12px", maxWidth: "90%",
                }}>
                  {m.contenu}
                </span>
              </Box>
            )
          }

          return (
            <React.Fragment key={m.id}>
              {nouveauJour && (
                <Box style={{ textAlign: "center", margin: "12px 0 10px" }}>
                  <span style={{
                    fontSize: 11, color: "#9A938C", backgroundColor: "#F1EDE9",
                    borderRadius: 20, padding: "3px 12px", textTransform: "capitalize",
                  }}>
                    {jourDe(m.quand)}
                  </span>
                </Box>
              )}
              <Box
                className="supportchat-anime"
                style={{
                  display: "flex",
                  flexDirection: m.role === "user" ? "row-reverse" : "row",
                  alignItems: "flex-end", gap: 8,
                  marginTop: memeAuteurQuAvant ? 3 : 12,
                  animation: "supportchat-pop 0.25s ease-out",
                }}
              >
                {m.role !== "user" && (
                  <Box style={{ visibility: memeAuteurQuAvant ? "hidden" : "visible" }}>
                    <Avatar />
                  </Box>
                )}
                <Box style={{ maxWidth: "78%" }}>
                  {!memeAuteurQuAvant && (
                    <div style={{
                      fontSize: 11, color: "#9A938C", marginBottom: 3,
                      textAlign: m.role === "user" ? "right" : "left",
                      padding: m.role === "user" ? "0 4px 0 0" : "0 0 0 4px",
                    }}>
                      <span style={{ fontWeight: 600, color: "#6E675F" }}>{auteurDe(m)}</span>
                      {" · "}{heureDe(m.quand)}
                    </div>
                  )}
                  <Box
                    title={heureDe(m.quand)}
                    style={{
                      padding: "9px 13px", fontSize: 14, lineHeight: 1.45,
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                      backgroundColor: m.role === "user" ? "#000" : "#fff",
                      color: m.role === "user" ? "#fff" : "#0A0A0B",
                      border: m.role === "user" ? "none" : "1px solid #ECE7E3",
                      borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    }}
                  >
                    {m.contenu}
                  </Box>
                </Box>
              </Box>
            </React.Fragment>
          )
        })}

        {enCours && (
          <Box style={{ display: "flex", alignItems: "flex-end", gap: 8, marginTop: 12 }}>
            <Avatar />
            <Box style={{
              display: "flex", gap: 4, alignItems: "center",
              backgroundColor: "#fff", border: "1px solid #ECE7E3",
              borderRadius: "14px 14px 14px 4px", padding: "12px 14px",
            }}>
              {[0, 1, 2].map((n) => (
                <span
                  key={n}
                  className="supportchat-anime"
                  style={{
                    width: 6, height: 6, borderRadius: "50%", backgroundColor: "#9A938C",
                    display: "inline-block",
                    animation: `supportchat-dot 1.1s ${n * 0.18}s infinite ease-in-out`,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        <div ref={finDuFil} />
      </Box>

      <Box style={{ display: "flex", alignItems: "center", gap: 4, padding: 8, borderTop: "1px solid #ECE7E3", backgroundColor: "#fff" }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t.saisie}
          value={saisie}
          disabled={enCours}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            // Entree envoie, Maj+Entree passe a la ligne : convention attendue
            // dans un chat, evite les envois accidentels a mi-phrase.
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); envoyer() }
          }}
          inputProps={{ maxLength: 2000, "aria-label": t.saisie }}
        />
        <IconButton
          aria-label={t.envoyer}
          onClick={envoyer}
          disabled={enCours || !saisie.trim()}
          style={{ backgroundColor: saisie.trim() && !enCours ? "#000" : "#EDEAE6", color: saisie.trim() && !enCours ? "#fff" : "#9A938C", width: 38, height: 38 }}
        >
          <SendIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  )
}

export default SupportChat
